import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BeatLoader } from 'react-spinners';

import { getAllEvents, searchEvents } from '../../redux/actions/events';
import { listBrands, searchBrands } from '../../redux/actions/brands';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { useDelay } from 'hooks/useDelay';
import applyEventTimeFilters from 'utils/eventTimeFilters';

import EventCardWrapper from 'components/EventCardWrapper';
import EventCardList from 'components/EventCardList';
import BrandCardWrapper from 'components/BrandCardWrapper';
import BrandCardList from 'components/BrandCardList';
import EventSearchBar from 'components/EventSearchBar';
import MapButton from 'components/MapButton';
import SortBar from 'components/SortBar';

const LoadingOverlay = ({ label, layout = 'grid' }) => {
  const gridClass =
    layout === 'list'
      ? 'grid grid-cols-1 gap-y-2'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 auto-rows-fr items-stretch';

  return (
    <div className="absolute inset-0 z-10">
      <div className="absolute inset-0 bg-white/85 backdrop-blur-xl" />
      <div className="absolute inset-0 bg-gray-50/40" />
      <div className="absolute inset-0 opacity-[0.35] grayscale" />

      <div className="absolute inset-0 p-1 pointer-events-none">
        <div className={gridClass}>
          {Array.from({ length: layout === 'list' ? 8 : 12 }).map((_, i) => (
            <SkeletonCard
              key={i}
              variant={layout === 'list' ? 'list' : 'grid'}
            />
          ))}
        </div>
      </div>

      {/* <div className="absolute left-0 right-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent animate-pulse" /> */}

      <div className="absolute left-0 right-0 bottom-6 flex justify-center px-4">
        <div className="inline-flex items-center gap-3 rounded-full bg-white/95 shadow-lg ring-1 ring-black/5 px-4 py-2">
          <BeatLoader size={6} color="#FACC15" />
          <span className="text-xs font-medium text-gray-700">
            {label || 'Updating…'}
          </span>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = ({ variant = 'grid' }) => {
  if (variant === 'list') {
    return (
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
        <div className="flex gap-4 p-4">
          <div className="w-28 h-20 rounded-xl bg-gray-200 animate-pulse" />
          <div className="flex-1">
            <div className="h-4 w-2/3 rounded bg-gray-200 animate-pulse mb-2" />
            <div className="h-3 w-1/2 rounded bg-gray-200 animate-pulse mb-3" />
            <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
      <div className="h-44 bg-gray-200 animate-pulse" />
      <div className="p-4">
        <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse mb-2" />
        <div className="h-3 w-1/2 rounded bg-gray-200 animate-pulse mb-3" />
        <div className="flex gap-2">
          <div className="h-7 w-20 rounded-full bg-gray-200 animate-pulse" />
          <div className="h-7 w-24 rounded-full bg-gray-200 animate-pulse" />
        </div>
      </div>
    </div>
  );
};

const SkeletonGrid = ({ layout = 'grid', count = 12 }) => {
  const gridClass =
    layout === 'list'
      ? 'grid grid-cols-1 gap-y-2'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 auto-rows-fr items-stretch';

  return (
    <div className={gridClass}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} variant={layout === 'list' ? 'list' : 'grid'} />
      ))}
    </div>
  );
};

const FloatingMorePill = ({ label = 'Loading more…' }) => {
  return (
    <div className="flex justify-center py-6">
      <div className="inline-flex items-center gap-3 rounded-full bg-white shadow-md ring-1 ring-black/5 px-4 py-2">
        <BeatLoader size={6} color="#FACC15" />
        <span className="text-xs font-medium text-gray-700">{label}</span>
      </div>
    </div>
  );
};

const splitCsv = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const hasAny = (obj = {}) =>
  Object.values(obj).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

const FloatingMapButton = ({ to, text }) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setHidden(entry.isIntersecting),
      { rootMargin: '0px 0px -40px 0px', threshold: 0 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      aria-hidden={hidden}
      className={`pointer-events-none fixed bottom-0 left-0 right-0 z-[100] flex justify-center transition-all duration-300 ease-out ${
        hidden ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'
      }`}
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
    >
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-paper-bg/80 via-paper-bg/40 to-transparent" />
      <Link
        to={to}
        tabIndex={hidden ? -1 : 0}
        className="pointer-events-auto relative pb-6 pt-4 flex justify-center"
        aria-label={text}
      >
        <MapButton hasIcon text={text} />
      </Link>
    </div>
  );
};

const ListingPage = () => {
  const [displayType, setDisplayType] = useState('squared');
  const [selectedSort, setSelectedSort] = useState('by-date');

  const lastBrandFiltersRef = useRef({});
  const bottomRef = useRef(null);
  const observerRef = useRef(null);

  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const searchType = params.get('searchType') || 'events';

  const eventSearchFromUrl = useMemo(() => {
    const q = params.get('q') || '';
    const qMacroCategories = splitCsv(params.get('qMacroCategories'));
    const qMicroCategories = splitCsv(params.get('qMicroCategories'));
    const city = params.get('city') || '';
    const country = params.get('country') || '';
    const countryCode = params.get('countryCode') || '';
    const locationValue = params.get('location') || '';
    const locationLabel = params.get('locationLabel') || '';
    const startDate = params.get('startDate') || '';
    const endDate = params.get('endDate') || '';
    const monthFrom = params.get('monthFrom') || '';
    const monthTo = params.get('monthTo') || '';
    const macroCategories = splitCsv(params.get('macroCategories'));
    const microCategories = splitCsv(params.get('microCategories'));
    const utilities = splitCsv(params.get('utilities'));
    const maxPriceRaw = params.get('maxPrice');
    const maxPrice =
      maxPriceRaw === null || maxPriceRaw === '' ? null : Number(maxPriceRaw);

    const filters = {};
    if (q) filters.q = q;
    if (qMacroCategories.length) filters.qMacroCategories = qMacroCategories;
    if (qMicroCategories.length) filters.qMicroCategories = qMicroCategories;
    if (city) filters.city = city;
    if (country) filters.country = country;
    if (countryCode) filters.countryCode = countryCode;
    if (locationValue) filters.location = locationValue;
    if (locationLabel) filters.locationLabel = locationLabel;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (monthFrom) filters.monthFrom = monthFrom;
    if (monthTo) filters.monthTo = monthTo;
    if (macroCategories.length) filters.macroCategories = macroCategories;
    if (microCategories.length) filters.microCategories = microCategories;
    if (utilities.length) filters.utilities = utilities;
    if (Number.isFinite(maxPrice)) filters.maxPrice = maxPrice;
    // hidePastEvents is intentionally not added here: it defaults to true and
    // would otherwise make every listing visit count as an "active search",
    // forcing the paginated list endpoint into the search path. We pass it
    // separately to both list and search requests below.

    return filters;
  }, [params]);

  const brandSearchFromUrl = useMemo(() => {
    const q = params.get('q') || '';
    const qMacroCategories = splitCsv(params.get('qMacroCategories'));
    const qMicroCategories = splitCsv(params.get('qMicroCategories'));
    const macroCategories = splitCsv(params.get('macroCategories'));
    const microCategories = splitCsv(params.get('microCategories'));
    const countryCode = params.get('countryCode') || '';
    const city = params.get('city') || '';
    const country = params.get('country') || '';
    const locationLabel = params.get('locationLabel') || '';

    const filters = {};
    if (q) filters.q = q;
    if (qMacroCategories.length) filters.qMacroCategories = qMacroCategories;
    if (qMicroCategories.length) filters.qMicroCategories = qMicroCategories;
    if (macroCategories.length) filters.macroCategories = macroCategories;
    if (microCategories.length) filters.microCategories = microCategories;
    if (countryCode) filters.countryCode = countryCode;
    if (city) filters.city = city;
    if (country) filters.country = country;
    if (locationLabel) filters.locationLabel = locationLabel;

    return filters;
  }, [params]);

  const hasEventSearchFromUrl = useMemo(
    () => hasAny(eventSearchFromUrl),
    [eventSearchFromUrl]
  );
  const hasBrandSearchFromUrl = useMemo(
    () => hasAny(brandSearchFromUrl),
    [brandSearchFromUrl]
  );

  const searchBarInitialState = useMemo(() => {
    if (searchType === 'events') {
      return {
        query: params.get('q') || '',
        location: {
          label: pickFirst(
            params.get('locationLabel'),
            params.get('location'),
            ''
          ),
          primary: params.get('location') || '',
          city: params.get('city') || '',
          country: params.get('country') || '',
          countryCode: params.get('countryCode') || '',
          placeId: '',
        },
        selectedDates: {
          from: params.get('startDate') || '',
          to: params.get('endDate') || '',
        },
        selectedFilters: {
          macroCategories: splitCsv(params.get('macroCategories')),
          microCategories: splitCsv(params.get('microCategories')),
          utilities: splitCsv(params.get('utilities')),
          maxPrice:
            params.get('maxPrice') !== null &&
            params.get('maxPrice') !== '' &&
            Number.isFinite(Number(params.get('maxPrice')))
              ? Number(params.get('maxPrice'))
              : null,
          hidePastEvents: params.get('hidePastEvents') !== 'false',
          hideLiveEvents: params.get('hideLiveEvents') === 'true',
          monthRange: {
            from: params.get('monthFrom') || '',
            to: params.get('monthTo') || '',
          },
        },
        hasActiveSearch: hasEventSearchFromUrl,
      };
    }

    return {
      query: params.get('q') || '',
      location: {
        label: pickFirst(
          params.get('locationLabel'),
          params.get('country'),
          ''
        ),
        primary: params.get('country') || '',
        city: '',
        country: params.get('country') || '',
        countryCode: params.get('countryCode') || '',
        placeId: '',
      },
      selectedDates: { from: '', to: '' },
      selectedFilters: {
        macroCategories: splitCsv(params.get('macroCategories')),
        microCategories: splitCsv(params.get('microCategories')),
        utilities: [],
        maxPrice: null,
      },
      hasActiveSearch: hasBrandSearchFromUrl,
    };
  }, [params, searchType, hasEventSearchFromUrl, hasBrandSearchFromUrl]);

  const canUseList = useMediaQuery('(min-width: 1024px)');

  const { isAuthenticated } = useSelector((state) => state.auth || {});

  const {
    events = [],
    loading: loadingEvents = false,
    eventsPagination = null,
  } = useSelector((state) => state.events || {});

  const {
    brands: allBrands = [],
    brandsPagination = null,
    searchResults = null,
    searchPagination = null,
    loading: loadingBrandsList = false,
    searchLoading: loadingBrandsSearch = false,
  } = useSelector((state) => state.brands || {});

  const loadingBrands = loadingBrandsList || loadingBrandsSearch;
  const isLoadingAny = Boolean(loadingEvents || loadingBrands);
  const isBrandSearching = hasBrandSearchFromUrl && Boolean(searchResults);

  const effectiveDisplayType = useMemo(() => {
    return displayType === 'list' && canUseList ? 'list' : 'squared';
  }, [displayType, canUseList]);

  const isPriceSort =
    selectedSort === 'high-to-low' || selectedSort === 'low-to-high';

  useEffect(() => {
    // Guests can't use price sorts; fall back to the only guest option (T2B-355).
    if (!isAuthenticated && isPriceSort) setSelectedSort('by-date');
  }, [isAuthenticated, isPriceSort]);

  // Default ON: only `?hidePastEvents=false` opts out.
  const hidePastEventsFilter = params.get('hidePastEvents') !== 'false';
  // Default OFF: only `?hideLiveEvents=true` opts in.
  const hideLiveEventsFilter = params.get('hideLiveEvents') === 'true';

  const mapEventSort = useCallback((sort) => {
    switch (sort) {
      case 'oldest':
        return { sortBy: 'createdAt', sortOrder: 'asc' };
      case 'newest':
        return { sortBy: 'createdAt', sortOrder: 'desc' };
      case 'high-to-low':
        return { sortBy: 'lowestPrice', sortOrder: 'desc' };
      case 'low-to-high':
        return { sortBy: 'lowestPrice', sortOrder: 'asc' };
      case 'by-date':
      default:
        return { sortBy: 'startDate', sortOrder: 'asc' };
    }
  }, []);

  const mapBrandSort = useCallback((sort) => {
    switch (sort) {
      case 'oldest':
        return { sort: undefined, sortBy: 'registeredDate', sortOrder: 'asc' };
      case 'a-z':
        return { sort: 'a-z', sortBy: 'username', sortOrder: 'asc' };
      case 'z-a':
        return { sort: 'z-a', sortBy: 'username', sortOrder: 'desc' };
      default:
        return { sort: undefined, sortBy: 'registeredDate', sortOrder: 'desc' };
    }
  }, []);

  useEffect(() => {
    if (searchType !== 'events' || hasEventSearchFromUrl) return;
    const { sortBy, sortOrder } = mapEventSort(selectedSort);
    dispatch(
      getAllEvents({
        page: 1,
        pageSize: 12,
        sortBy,
        sortOrder,
        hidePastEvents: hidePastEventsFilter,
        hideLiveEvents: hideLiveEventsFilter,
        append: false,
        lang: i18n.language,
      })
    );
  }, [
    dispatch,
    searchType,
    selectedSort,
    mapEventSort,
    hasEventSearchFromUrl,
    hidePastEventsFilter,
    hideLiveEventsFilter,
    i18n.language,
  ]);

  useEffect(() => {
    if (searchType !== 'events' || !hasEventSearchFromUrl) return;
    const { sortBy, sortOrder } = mapEventSort(selectedSort);
    dispatch(
      searchEvents({
        ...eventSearchFromUrl,
        hidePastEvents: hidePastEventsFilter,
        hideLiveEvents: hideLiveEventsFilter,
        sortBy,
        sortOrder,
        lang: i18n.language,
      })
    );
  }, [
    dispatch,
    searchType,
    selectedSort,
    mapEventSort,
    hasEventSearchFromUrl,
    eventSearchFromUrl,
    hidePastEventsFilter,
    hideLiveEventsFilter,
    i18n.language,
  ]);

  useEffect(() => {
    if (searchType !== 'brands' || hasBrandSearchFromUrl) return;
    const m = mapBrandSort(selectedSort);

    lastBrandFiltersRef.current = {};
    dispatch(
      listBrands({
        page: 1,
        pageSize: 12,
        ...m,
        append: false,
        lang: i18n.language,
      })
    );
  }, [
    dispatch,
    searchType,
    selectedSort,
    mapBrandSort,
    hasBrandSearchFromUrl,
    i18n.language,
  ]);

  useEffect(() => {
    if (searchType !== 'brands' || !hasBrandSearchFromUrl) return;
    const m = mapBrandSort(selectedSort);
    lastBrandFiltersRef.current = { ...brandSearchFromUrl };

    dispatch(
      searchBrands(brandSearchFromUrl, {
        page: 1,
        pageSize: 12,
        ...m,
        append: false,
        lang: i18n.language,
      })
    );
  }, [
    dispatch,
    searchType,
    selectedSort,
    mapBrandSort,
    hasBrandSearchFromUrl,
    brandSearchFromUrl,
    i18n.language,
  ]);

  useEffect(() => {
    if (!canUseList && displayType === 'list') setDisplayType('squared');
  }, [canUseList, displayType]);

  const brandsList = useMemo(
    () => (isBrandSearching ? searchResults : allBrands),
    [isBrandSearching, searchResults, allBrands]
  );

  const rawItems = useMemo(() => {
    if (searchType === 'events') {
      return applyEventTimeFilters(events, {
        hidePastEvents: hidePastEventsFilter,
        hideLiveEvents: hideLiveEventsFilter,
      });
    }
    return brandsList || [];
  }, [
    searchType,
    events,
    brandsList,
    hidePastEventsFilter,
    hideLiveEventsFilter,
  ]);

  const hasNext = useMemo(() => {
    if (searchType === 'events') return Boolean(eventsPagination?.hasNext);
    const pag = isBrandSearching ? searchPagination : brandsPagination;
    return Boolean(pag?.hasNext);
  }, [
    searchType,
    eventsPagination,
    isBrandSearching,
    searchPagination,
    brandsPagination,
  ]);

  // --- Premium: cache last successful lists per tab so we never flash empty UI
  const cacheRef = useRef({ events: [], brands: [] });

  useEffect(() => {
    if (!isLoadingAny && rawItems.length > 0) {
      cacheRef.current[searchType] = rawItems;
    }
  }, [isLoadingAny, rawItems, searchType]);

  // --- Detect "view switches" (tab/sort changes) so we can show overlay instantly
  const viewKey = useMemo(() => {
    const mode =
      searchType === 'brands'
        ? isBrandSearching
          ? 'search'
          : 'list'
        : 'events';
    // Include the current filter signature so cache/scroll/overlay all
    // reset when the user changes any filter in EventSearchBar/FiltersModal.
    const filterSig =
      searchType === 'events'
        ? JSON.stringify(eventSearchFromUrl)
        : JSON.stringify(brandSearchFromUrl);
    return `${searchType}|${selectedSort}|${mode}|${filterSig}`;
  }, [
    searchType,
    selectedSort,
    isBrandSearching,
    eventSearchFromUrl,
    brandSearchFromUrl,
  ]);

  const prevViewKeyRef = useRef(viewKey);
  const [switchLoading, setSwitchLoading] = useState(false);

  useEffect(() => {
    if (prevViewKeyRef.current === viewKey) return;
    prevViewKeyRef.current = viewKey;
    setSwitchLoading(true);

    // Invalidate the cached items for the current tab — otherwise
    // `displayItems` will keep showing accumulated ASC pages while the new
    // DESC fetch is loading, which looks like stale/mixed results.
    cacheRef.current[searchType] = [];

    // Reset scroll so the user lands at the top of the new sorted list
    // instead of at the (now-empty) bottom of the previous one.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    // auto-release switchLoading once request finishes (or after a safety timeout)
    const safety = setTimeout(() => setSwitchLoading(false), 1500);
    return () => clearTimeout(safety);
  }, [viewKey, searchType]);

  useEffect(() => {
    if (!switchLoading) return;
    if (!isLoadingAny) setSwitchLoading(false);
  }, [switchLoading, isLoadingAny]);

  // Use cache while loading (so UI feels instant)
  const displayItems = useMemo(() => {
    if (rawItems.length) return rawItems;
    if (
      (isLoadingAny || switchLoading) &&
      cacheRef.current[searchType]?.length
    ) {
      return cacheRef.current[searchType];
    }
    return rawItems;
  }, [rawItems, isLoadingAny, switchLoading, searchType]);

  const isShowingCached =
    rawItems.length === 0 &&
    displayItems.length > 0 &&
    (isLoadingAny || switchLoading);

  // --- Load more (infinite scroll) state
  const nextReqRef = useRef({ key: '', page: 0 });
  const inFlightRef = useRef(false);
  const [loadingMoreUi, setLoadingMoreUi] = useState(false);

  // Reset infinite-scroll guards when the fresh-fetch signature changes.
  // Without this, an in-flight load-more request that gets aborted by
  // a new initial fetch (e.g. user clicking the magnifying glass mid-scroll)
  // can leave `inFlightRef` set to true past the moment the observer
  // re-fires, silently blocking pagination until the user scrolls again.
  const fetchSignature = `${searchType}|${selectedSort}|${hidePastEventsFilter}|${hideLiveEventsFilter}|${JSON.stringify(
    searchType === 'events' ? eventSearchFromUrl : brandSearchFromUrl
  )}`;
  useEffect(() => {
    nextReqRef.current = { key: '', page: 0 };
    inFlightRef.current = false;
    setLoadingMoreUi(false);
  }, [fetchSignature]);

  const loadNextPage = useCallback(() => {
    if (!hasNext || isLoadingAny || inFlightRef.current) return;

    const key =
      searchType === 'events'
        ? `events|${selectedSort}`
        : `brands|${selectedSort}|${isBrandSearching ? 'search' : 'list'}|${JSON.stringify(
            lastBrandFiltersRef.current || {}
          )}`;

    if (searchType === 'events') {
      const nextPage = (eventsPagination?.page || 1) + 1;
      if (
        nextReqRef.current.key === key &&
        nextReqRef.current.page === nextPage
      )
        return;

      nextReqRef.current = { key, page: nextPage };
      inFlightRef.current = true;
      setLoadingMoreUi(true);

      const { sortBy, sortOrder } = mapEventSort(selectedSort);
      dispatch(
        getAllEvents({
          page: nextPage,
          pageSize: eventsPagination?.pageSize || 12,
          sortBy,
          sortOrder,
          hidePastEvents: hidePastEventsFilter,
          hideLiveEvents: hideLiveEventsFilter,
          append: true,
          lang: i18n.language,
        })
      ).finally(() => {
        inFlightRef.current = false;
        setLoadingMoreUi(false);
      });
      return;
    }

    const pag = isBrandSearching ? searchPagination : brandsPagination;
    const nextPage = (pag?.page || 1) + 1;
    if (nextReqRef.current.key === key && nextReqRef.current.page === nextPage)
      return;

    nextReqRef.current = { key, page: nextPage };
    inFlightRef.current = true;
    setLoadingMoreUi(true);

    const m = mapBrandSort(selectedSort);
    const thunk = isBrandSearching
      ? searchBrands(lastBrandFiltersRef.current, {
          page: nextPage,
          pageSize: pag?.pageSize || 12,
          ...m,
          append: true,
          lang: i18n.language,
        })
      : listBrands({
          page: nextPage,
          pageSize: pag?.pageSize || 12,
          ...m,
          append: true,
          lang: i18n.language,
        });

    dispatch(thunk).finally(() => {
      inFlightRef.current = false;
      setLoadingMoreUi(false);
    });
  }, [
    hasNext,
    isLoadingAny,
    searchType,
    selectedSort,
    isBrandSearching,
    eventsPagination,
    brandsPagination,
    searchPagination,
    dispatch,
    mapEventSort,
    mapBrandSort,
    hidePastEventsFilter,
    hideLiveEventsFilter,
    i18n.language,
  ]);

  useEffect(() => {
    if (!bottomRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first && first.isIntersecting) loadNextPage();
      },
      { root: null, rootMargin: '1200px 0px', threshold: 0.01 }
    );

    observerRef.current.observe(bottomRef.current);
    return () => observerRef.current && observerRef.current.disconnect();
  }, [loadNextPage, displayItems.length, hasNext, isLoadingAny]);

  // Premium empty state: only show after we’re sure we’re not loading
  const showEmptyState = useDelay(
    !isLoadingAny && !switchLoading && displayItems.length === 0,
    200
  );

  const reachedEnd = !isLoadingAny && !hasNext && displayItems.length > 0;

  const loadingLabel =
    searchType === 'events' ? 'Loading events…' : 'Loading brands…';

  const renderCards = useCallback(() => {
    const Wrapper =
      searchType === 'events' ? EventCardWrapper : BrandCardWrapper;
    const ListComponent =
      searchType === 'events' ? EventCardList : BrandCardList;

    const isInitialLoading =
      (isLoadingAny || switchLoading) && displayItems.length === 0;

    // 1) First load: skeletons (feels premium)
    if (isInitialLoading) {
      return (
        <SkeletonGrid
          layout={effectiveDisplayType === 'list' ? 'list' : 'grid'}
          count={effectiveDisplayType === 'list' ? 8 : 12}
        />
      );
    }

    // 2) Empty state (only when confirmed)
    if (displayItems.length === 0) {
      if (!showEmptyState) {
        return (
          <SkeletonGrid
            layout={effectiveDisplayType === 'list' ? 'list' : 'grid'}
            count={8}
          />
        );
      }

      return (
        <p className="text-center text-gray-500 text-base md:text-lg py-10">
          {searchType === 'events'
            ? t('listing.no_events')
            : t('listing.no_brands')}
        </p>
      );
    }

    // 3) Normal render
    const showList = effectiveDisplayType === 'list';

    const gridClass =
      effectiveDisplayType === 'squared'
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 auto-rows-fr items-stretch'
        : 'gap-y-2 grid-cols-1';

    // Overlay when refreshing (sort/tab/search), but NOT when infinite scrolling
    const showOverlay =
      (switchLoading || isShowingCached || isLoadingAny) && !loadingMoreUi;

    return (
      <div className="relative">
        {showOverlay && (
          <LoadingOverlay
            label={loadingLabel}
            layout={effectiveDisplayType === 'list' ? 'list' : 'grid'}
          />
        )}

        <div className={`grid ${gridClass}`}>
          {displayItems.map((item) =>
            effectiveDisplayType === 'squared' ? (
              <Wrapper
                key={item.id}
                hasNavigation
                event={searchType === 'events' ? item : undefined}
                brand={searchType === 'brands' ? item : undefined}
                isAuthenticated={isAuthenticated}
                small={false}
                lang={i18n.language}
                eventsCount={item.eventsCount}
              />
            ) : showList ? (
              <ListComponent
                key={item.id}
                hasNavigation
                event={searchType === 'events' ? item : undefined}
                brand={searchType === 'brands' ? item : undefined}
                isGuest={!isAuthenticated}
              />
            ) : null
          )}
        </div>

        {/* premium load-more */}
        {loadingMoreUi && <FloatingMorePill label="Loading more…" />}

        {reachedEnd && (
          <div className="text-center text-gray-500 text-sm py-6">
            {t('listing.end_of_results') || 'You’ve reached the end'}
          </div>
        )}

        <div ref={bottomRef} className="h-1 w-full" />
      </div>
    );
  }, [
    searchType,
    isLoadingAny,
    switchLoading,
    displayItems,
    effectiveDisplayType,
    isAuthenticated,
    loadingMoreUi,
    reachedEnd,
    t,
    showEmptyState,
    isShowingCached,
    loadingLabel,
    i18n.language,
  ]);

  return (
    <div className="bg-gray-100 min-h-screen pb-24 px-2 sm:px-4 md:px-8">
      <div className="flex flex-col items-center justify-center py-4 sm:py-6">
        <EventSearchBar
          currentSearch={searchType}
          hasLabel={false}
          isAuthenticated={isAuthenticated}
          initialState={searchBarInitialState}
          redirectToListings
        />
      </div>

      <div className="flex justify-center">
        <div className="w-full max-w-7xl">
          <div className="mb-2 px-1">
            <SortBar
              displayType={displayType}
              setDisplayType={setDisplayType}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              searchType={searchType}
              isDesktop={canUseList}
              isAuthenticated={isAuthenticated}
              hidePastEvents={hidePastEventsFilter}
            />
          </div>

          {renderCards()}
        </div>
      </div>

      {searchType === 'events' && (
        <FloatingMapButton to="/events/map" text={t('listing.map_button')} />
      )}
    </div>
  );
};

export default ListingPage;
